import { accountKeys, type Capability, prepaidRecognitionKeys } from '@taler/api-client'
import type { LocalDate } from '@taler/domain'
import { defineEntryExtension, type RegisteredExtension } from '@/features/entries'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/app/api'
import PrepaidScheduleRowDetail from '@/features/prepaid-recognitions/components/PrepaidScheduleRowDetail.vue'
import { canScheduleRecognitions } from '../internal/capabilities.ts'
import { type ExpenseAccountOption, expenseAccountOptions } from '../internal/expense-accounts.ts'
import { isPrepaidExpenseAccount } from '../internal/prepaid-accounts.ts'

export interface UsePrepaidScheduleExtensionOptions {
  readonly ledgerId: () => string | undefined
  readonly can: (capability: Capability) => boolean
  readonly locale: () => 'en' | 'hu'
}

export interface PrepaidScheduleMatch {
  readonly key: string
  readonly label: string
  readonly expenseAccounts: readonly ExpenseAccountOption[]
}

export interface PrepaidScheduleDraft {
  readonly expenseAccountId: string | undefined
  readonly periods: string
  /** Undefined follows the row's own date. */
  readonly startDate: LocalDate | undefined
}

const DEFAULT_PERIODS = '12'

export function parsePeriods(periods: string): number | undefined {
  if (!/^\d+$/.test(periods.trim())) return undefined
  const count = parseInt(periods, 10)
  return count >= 1 ? count : undefined
}

/** A payment into the prepaid-expenses account is recognised over time, scheduled once the entry posts. */
export function usePrepaidScheduleExtension(
  options: UsePrepaidScheduleExtensionOptions,
): RegisteredExtension {
  const api = useApi()
  const queryClient = useQueryClient()
  const { t } = useI18n()
  const enabled = computed(() => canScheduleRecognitions(options.can) && options.ledgerId() !== undefined)

  const treeQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled,
    retry: false,
  })
  const expenseAccounts = computed(() => expenseAccountOptions(treeQuery.data.value ?? [], options.locale()))

  return defineEntryExtension<PrepaidScheduleMatch, PrepaidScheduleDraft>({
    id: 'prepaid-schedule',
    detect: (view) => {
      const into = view.counters[0]
      if (!enabled.value || view.kind !== 'TRANSFER' || !isPrepaidExpenseAccount(into?.codePath.at(-1))) {
        return undefined
      }
      return {
        key: 'prepaid',
        label: t('prepaidRecognitions.extension.label'),
        expenseAccounts: expenseAccounts.value,
      }
    },
    carries: new Set(['payee', 'memo', 'tags']),
    owns: (target) => isPrepaidExpenseAccount(target.codePath.at(-1)),
    initialDraft: () => ({ expenseAccountId: undefined, periods: DEFAULT_PERIODS, startDate: undefined }),
    problems: (_match, draft) => [
      ...(draft.expenseAccountId === undefined
        ? [{ severity: 'needs-details' as const, message: t('prepaidRecognitions.extension.chooseExpense') }]
        : []),
      ...(parsePeriods(draft.periods) === undefined
        ? [{ severity: 'needs-details' as const, message: t('prepaidRecognitions.extension.invalidPeriods') }]
        : []),
    ],
    detail: PrepaidScheduleRowDetail,
    placement: 'inline',
    submit: {
      shape: 'attach',
      run: async (posted, _match, draft, view) => {
        const ledgerId = options.ledgerId()!
        await api.prepaidRecognitions.scheduleRecognition(ledgerId, {
          originEntryId: posted.id,
          expenseAccountId: draft.expenseAccountId!,
          periods: parsePeriods(draft.periods)!,
          startDate: draft.startDate ?? view.row.date,
        })
        for (const queryKey of prepaidRecognitionKeys.invalidatedByWrite(ledgerId, posted.id)) {
          void queryClient.invalidateQueries({ queryKey })
        }
      },
    },
  })
}
